/**
 * Client-side toast notifications driven by `PropertyView` state. Fires when:
 *  - A property crosses an idle threshold (5 days in stage)
 *  - A new stage is reached
 *  - A "clear to close" milestone is achieved
 *
 * Persistence: per-session via a Set in the hook + localStorage flags so we
 * don't re-toast the same milestone after a page reload.
 *
 * TODO server: the spec calls out emails for several triggers. Email side is
 * handled by the backend notifications service in a later phase; here we just
 * fire client-side `sonner` toasts.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { idleDismissedStore } from '@/utils/propertyView/localStateStore';
import type { PropertyView } from '@/types/PropertyView';

const STAGE_KEY = 'unifiedProperty.toastedStages.v1';
/** Idle nudges already shown this browser session (survives page changes). */
const IDLE_SESSION_KEY = 'unifiedProperty.toastedIdle.v1';
const CTC_KEY = 'unifiedProperty.toastedCtc.v1';

function readSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function writeSet(key: string, set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

function readSessionSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    return new Set(JSON.parse(window.sessionStorage.getItem(key) || '[]'));
  } catch {
    return new Set();
  }
}

function writeSessionSet(key: string, set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

/**
 * `isLoading` must be false before anything fires: while the application,
 * contract and pipeline load, the resolved stage climbs (3 → 6 → 7), which
 * used to toast "reached Stage N" for each step and the idle nudge 3 times.
 */
export function useStageNotifications(
  property: PropertyView | undefined,
  isLoading = false
): void {
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!property || isLoading) return;
    const stageKey = `${property.id}:${property.stage}`;
    const stageSet = readSet(STAGE_KEY);
    const localSeen = seenRef.current;

    if (!localSeen.has(`stage:${stageKey}`) && !stageSet.has(stageKey)) {
      localSeen.add(`stage:${stageKey}`);
      stageSet.add(stageKey);
      writeSet(STAGE_KEY, stageSet);
      if (property.stage > 1) {
        toast.success(
          `${property.address || 'Property'} reached Stage ${property.stage}`
        );
      }
    }

    if (property.isIdle && !idleDismissedStore.has(property.id)) {
      // Once per property per session — not once per stage page visited.
      const idleKey = `${property.id}:idle`;
      const idleSeen = readSessionSet(IDLE_SESSION_KEY);
      if (!idleSeen.has(idleKey)) {
        idleSeen.add(idleKey);
        writeSessionSet(IDLE_SESSION_KEY, idleSeen);
        toast.warning(
          `${property.address || 'Property'} has been idle for ${
            property.daysInCurrentStage
          } days — take the next step?`
        );
      }
    }

    if (
      property.pipelineCurrentStageKey?.toLowerCase().includes('clear_to_close')
    ) {
      const ctcKey = `${property.id}:ctc`;
      const ctcSet = readSet(CTC_KEY);
      if (!ctcSet.has(ctcKey) && !localSeen.has(ctcKey)) {
        localSeen.add(ctcKey);
        ctcSet.add(ctcKey);
        writeSet(CTC_KEY, ctcSet);
        toast.success('Clear to Close! Your loan is fully approved.');
      }
    }
  }, [property, isLoading]);
}
